// @flow
import * as React from "react";
import idx from "idx";
import { rgba } from "polished";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import Gettext from "../../../common/Gettext";
import withData from "../../../withData";
import Button from "../../../Button";
import { FlatButton } from "../../../Buttons";
import HowMuchInBucks from "../../../HowMuchInBucks";
import Tournament from "../../Api";
import { getActive, mapStateToTime } from "../../Timer";
import { ModalConsumer, type ShowModal } from "../../../common/ModalProvider";
import * as ErrorPopups from "../../Popups/Error";
import { fontSize } from "../../../common/styled-mixins";
import DemoButton from "../../../Buttons/DemoTournamentButton";

import type {
  Account,
  Tournament as TournamentProps
} from "../../../../redux/CommonTypes";

const Cost = styled.span`
  margin: 0 5px;
  font-size: ${props =>
    props.type === "free" ? fontSize(16, 16) : fontSize(10, 10)};
  color: ${({ theme }) =>
    theme.mode === "light" ? "#ffffff" : theme.indexMajor};
  text-shadow: none;

  ${({ isCardButton, isTradePageCard }) =>
    (isTradePageCard || isCardButton) &&
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
    css`
      color: inherit;
    `};
`;

const ButtonContent = styled.span`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  align-content: center;
  font-size: ${fontSize(process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ? 12 : 16)};

  ${({ flat }) =>
    !flat &&
    css`
      flex-direction: column;

      ${Cost} {
        margin-top: 5px;
      }
    `};
`;

const ButtonPlaceholder = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  min-height: 37px;
  padding: 0 14px;
  font-weight: 700;
  font-size: ${fontSize(12)};
  line-height: 14px;
  color: ${({ theme }) => theme.tournamentCardPlaceholder};
  text-align: center;

  ${({ isTradePageCard }) =>
    isTradePageCard &&
    css`
      font-size: 22px;
      line-height: 1.2;
      font-weight: bold;
      color: ${rgba("#ffffff", 0.44)};
      text-align: center;
      text-transform: uppercase;
    `};
`;

const NotAllowedPlaceholder = styled.div`
  margin-left: 5px;
  padding: 0;
  font-weight: inherit;
  color: currentColor;
`;

type ButtonProps = {
  ...TournamentProps,
  onJoinClick: () => void,
  onActiveTradeClick: () => void,
  onTradeClick: () => void,
  onLeaveClick: () => void,
  time?: string,
  active?: boolean,
  flat?: boolean,
  className?: string,
  usePlaceholders?: boolean,
  accounts: Array<Account>,
  api: any,
  enabledStock?: boolean,
  isCardButton?: boolean,
  newStyle?: boolean,
  forBell ?: boolean
};

function RawBtnSwitcher({
  api,
  usePlaceholders = false,
  flat = false,
  className,
  accounts,
  enabledStock = true,
  newStyle,
  subtype,
  ...props
}: ButtonProps) {
  let active_tournament_id = idx(
    api.readEntity("/api/tournaments/stats"),
    stats => stats.value.active_tournament.id
  );

  const realAccount =
    (accounts && accounts.find(account => account.type === "real")) || null;
  const active = props.active ? props.active : getActive(props);
  const ButtonComponent =
    subtype === "demo" && props.participation_status !== 1
      ? DemoButton
      : flat
      ? FlatButton
      : Button;
  const isCardButton = props.isCardButton && process.env.REACT_APP_DEPLOY_TARGET === "irontrade";
  const isTradePageCard = props.isTradePageCard && process.env.REACT_APP_DEPLOY_TARGET === "irontrade";
  if (
    !(
      (props.is_limited && props.limited_for_money) ||
      props.invited ||
      props.subtype === "demo"
    )
  ) {
    return (
      <NotAllowedPlaceholder>
        <Gettext>Available by invitation only</Gettext>
      </NotAllowedPlaceholder>
    );
  }

  let tournamentType = props.type;

  if (props.is_limited && props.invited) {
    tournamentType = "free";
  }

  if (props.participation_status !== 1) {
    return (
      <ModalConsumer>
        {({ showModal }: { showModal: ShowModal }) => (
          <ButtonComponent
            invited={props.is_limited && props.invited}
            subtype={props.subtype}
            isCardButton={isCardButton}
            isTradePageCard={isTradePageCard}
            color="green"
            type="deposit"
            withShadow={!flat}
            className={className}
            newStyle={newStyle}
            participation_price={props.participation_price}
            onClick={() => {
              if (tournamentType !== "free") {
                if (realAccount != null) {
                  const { balance } = realAccount;
                  if (
                    props.participation_price &&
                    balance < props.participation_price
                  ) {
                    showModal(ErrorPopups.NoRealAcc, { id: props.id });
                    return;
                  }
                } else {
                  showModal(ErrorPopups.NoRealAcc, { id: props.id });
                  return;
                }
              }

              props.onJoinClick();
            }}
            forBell={props.forBell}
          >
            <ButtonContent flat={flat}>
              {props.is_limited && props.invited ? (
                <Gettext>Use the invitation</Gettext>
              ) : (
                <Gettext>Join</Gettext>
              )}{" "}
              {props.is_limited && props.invited ? null : (
                <Cost
                  type={tournamentType}
                  isCardButton={isCardButton}
                  isTradePageCard={isTradePageCard}
                >
                  {tournamentType === "free" ? (
                    <Gettext>Free</Gettext>
                  ) : (
                    <HowMuchInBucks
                      value={props.participation_price}
                      valueUSD={props.participation_price_usd}
                    />
                  )}
                </Cost>
              )}
            </ButtonContent>
          </ButtonComponent>
        )}
      </ModalConsumer>
    );
  }

  if (props.participation_status === 1 && active === true) {
    const current = active_tournament_id === props.id;

    if (current && usePlaceholders) {
      if (isTradePageCard) {
        return (
          <ButtonPlaceholder isTradePageCard>
            <Gettext>You Are Trading</Gettext>
          </ButtonPlaceholder>
        );
      } else {
        return (
          <ButtonPlaceholder>
            <Gettext>You are participating in this tournament</Gettext>
          </ButtonPlaceholder>
        );
      }
    }

    return (
      <ButtonComponent
        isCardButton={isCardButton}
        color="yellow"
        themeLightGreen={props.themeLightGreen}
        type="trade"
        disabled={!enabledStock}
        withShadow={!flat}
        onClick={current ? props.onActiveTradeClick : props.onTradeClick}
        className={className}
        newStyle={newStyle}
        isTradePageCard={isTradePageCard}
      >
        <Gettext>Trade</Gettext>
      </ButtonComponent>
    );
  }

  if (props.participation_status === 1 && active === false) {
    if (usePlaceholders) {
      return (
        <ButtonPlaceholder>
          <Gettext>The tournament has not started yet</Gettext>
        </ButtonPlaceholder>
      );
    }

    return (
      <ButtonComponent
        isCardButton={isCardButton}
        className={className}
        newStyle={newStyle}
        onClick={() => {
          props.onLeaveClick();
        }}
      >
        <Gettext>Quit</Gettext>
      </ButtonComponent>
    );
  }

  return null;
}

const ButtonSwitcher = withData({
  required: ["stocks"],
  optional: ["accounts"],
  spinner: "simple"
})(
  connect(mapStateToTime)((props: ButtonProps) => (
    <Tournament.Consumer>
      {api => (
        <RawBtnSwitcher api={api} {...props} className="intercept-noauth" />
      )}
    </Tournament.Consumer>
  ))
);

export default ButtonSwitcher;



// WEBPACK FOOTER //
// ./src/components/Tournaments/Account/Card/ButtonSwitcher.js